import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  generateSummary,
  getLatestSummary,
  type InspectionSummary,
} from '../lib/notepad';

type Tab = 'ai' | 'raw';

export function Summary() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<InspectionSummary | null>(null);
  const [editedDraft, setEditedDraft] = useState('');
  const [tab, setTab] = useState<Tab>('ai');
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const latest = await getLatestSummary(jobId);
        if (cancelled) return;
        setSummary(latest);
        if (latest) {
          setEditedDraft(latest.edited_draft ?? latest.ai_draft ?? '');
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  async function runGenerate() {
    if (!jobId || generating) return;
    setGenerating(true);
    setErr(null);
    try {
      const next = await generateSummary(jobId);
      setSummary(next);
      setEditedDraft(next.ai_draft ?? '');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setGenerating(false);
    }
  }

  const draftWords = editedDraft.trim() ? editedDraft.trim().split(/\s+/).length : 0;

  return (
    <div className="mx-auto max-w-2xl p-4">
      <Link to={`/job/${jobId}/notepad`} className="text-sm text-gray-600 underline">
        ‹ Notepad
      </Link>
      <h1 className="mt-2 text-xl font-bold">Inspection summary</h1>

      {err && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* Tabs */}
      <div className="mt-4 flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setTab('ai')}
          className={`rounded-t-md px-3 py-2 text-sm font-semibold ${
            tab === 'ai'
              ? 'border-b-2 border-eva-teal text-eva-teal'
              : 'text-gray-500'
          }`}
        >
          AI Draft
        </button>
        <button
          onClick={() => setTab('raw')}
          className={`rounded-t-md px-3 py-2 text-sm font-semibold ${
            tab === 'raw'
              ? 'border-b-2 border-eva-teal text-eva-teal'
              : 'text-gray-500'
          }`}
        >
          Raw Notes
        </button>
      </div>

      {tab === 'ai' && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="rounded-full bg-bracket-amber/20 px-2 py-0.5 text-[10px] font-bold text-bracket-amber">
              AI DRAFT — EDIT BEFORE POSTING
            </span>
            <button
              onClick={() => void runGenerate()}
              disabled={generating}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1 text-xs font-semibold disabled:opacity-50"
            >
              {generating ? 'Generating…' : summary ? 'Redraft' : 'Generate'}
            </button>
          </div>
          {generating && !summary && (
            <div className="h-40 animate-pulse rounded-lg bg-gray-100" />
          )}
          {!generating && !summary && (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              No summary yet. Tap <strong>Generate</strong> to draft one from your
              notepad entries.
            </div>
          )}
          {summary && (
            <>
              <textarea
                value={editedDraft}
                onChange={(e) => setEditedDraft(e.target.value)}
                rows={14}
                className="w-full rounded-lg border border-gray-300 p-3 text-sm outline-none focus:border-eva-teal"
              />
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {draftWords} word{draftWords === 1 ? '' : 's'} · {editedDraft.length} chars
                </span>
                {summary.posted_to_eva_at && (
                  <span className="text-eva-teal">
                    Posted {new Date(summary.posted_to_eva_at).toLocaleString()}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'raw' && (
        <div className="mt-3 space-y-2">
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase text-gray-600">
            Sent as-is to EVA
          </span>
          <div className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
            {summary?.raw_notes_snapshot ?? '(generate a draft first)'}
          </div>
        </div>
      )}

      {summary && (
        <button
          onClick={() =>
            navigate(`/job/${jobId}/post-to-eva`, {
              state: { summary, editedDraft },
            })
          }
          disabled={!editedDraft.trim()}
          className="mt-6 w-full rounded-lg bg-eva-teal px-4 py-3 text-base font-semibold text-white disabled:opacity-40"
        >
          Post to EVA →
        </button>
      )}
    </div>
  );
}
